/**
 * The request processor of LSEG's RDP.Interp, ported from Workspace's Excel
 * add-in (Refinitiv for Office 26.30.2): it reads `x, X values, Y values,
 * Interpolation method` as LSEG does (its texts, its separators, its dates,
 * its skipping of blank pairs) and builds the request item. LSEG sends that
 * item to Adfin's `AdInterp`; here `interpLocal` answers it with adInterp
 * (interpMethods.ts), and the answer goes through the same response path as
 * the other AdFin functions.
 *
 * Behaviour the source does not settle is marked [A].
 */
import { AF_ARG, BaseDateProcessor } from "./baseProcessor";
import { adInterp, inputError, isPorted, LSEG_METHODS } from "./interpMethods";
import { isoToSerial } from "../lib/refinitiv";

const LIST_SEPARATORS = /[,;|]/;
/** A method as LSEG's pattern accepts it: a word, or two words joined by ":". */
// LSEG: InterpProcessor (37064).getInterpMethod (its pattern)
const METHOD_PATTERN = /^\w+(:\w+)?$/;
/** Two words and one of - _ | : \ / between them, spaces allowed around it. */
const TWO_WORDS = /^(\w+)\s*[-_|:\\/]\s*(\w+)$/;
const NUMERIC_TEXT = /^[+-]?(\d+\.?\d*|\.\d+)(e[+-]?\d+)?$/i;

/**
 * The method as LSEG reads it: upper-cased and trimmed, and `WORD <sep> WORD`
 * (sep one of - _ | : \ /, spaces allowed) written `WORD:WORD`, so "im_lin"
 * and "IM - LIN" are "IM:LIN". Text that is neither that nor a single word is
 * returned as typed, for the pattern to refuse ("im lin", "IM:LIN:X").
 */
// LSEG: InterpProcessor (37064).parseMethod
export function normaliseMethod(text: string): string {
  const upper = text.trim().toUpperCase();
  const words = TWO_WORDS.exec(upper);
  if (words) return `${words[1]}:${words[2]}`;
  return METHOD_PATTERN.test(upper) ? upper : text;
}

/** What InterpProcessor sends: `xValue` and the list entries are NaN where the input is invalid. */
export interface InterpRequest {
  xValue: number;
  xValues: number[];
  yValues: number[];
  interpMethod: string;
}

const isBlank = (cell: unknown): boolean => cell === null || cell === undefined || (typeof cell === "string" && cell.trim() === "");

// LSEG: InterpProcessor (37064).InterpProcessor
export class InterpProcessor extends BaseDateProcessor {
  constructor(args: unknown[]) {
    super(args);
    this.setArgIndexes([AF_ARG.xValue, AF_ARG.xValues, AF_ARG.yValues, AF_ARG.interpMethod]);
  }

  // LSEG: InterpProcessor (37064).prepareRequestInput
  prepareRequestInput(): Record<string, unknown> {
    const x = this.getXValue(this.getArg(AF_ARG.xValue));
    const xCells = this.getList(this.getArg(AF_ARG.xValues), "list of X values");
    const yCells = this.getList(this.getArg(AF_ARG.yValues), "list of Y values");
    const interpMethod = this.getInterpMethod(this.getArg(AF_ARG.interpMethod));
    const xValues: number[] = [];
    const yValues: number[] = [];
    // A pair with an empty X or an empty Y is skipped, so the points stay aligned.
    for (let i = 0; i < Math.max(xCells.length, yCells.length); i++) {
      if (isBlank(xCells[i]) || isBlank(yCells[i])) continue;
      xValues.push(nonNegative(this.toNumber(xCells[i])));
      yValues.push(this.toNumber(yCells[i]));
    }
    const minimum = interpMethod.startsWith("IM:TSPLINE") ? 3 : interpMethod.startsWith("IM:SPLREP") ? 4 : 2;
    this.adFinUtils.validate(
      xValues.length >= minimum,
      "list of X/Y values",
      `method ${interpMethod} requires at least ${minimum} interpolation points`,
    );
    return { xValue: x, xValues, yValues, interpMethod } satisfies InterpRequest;
  }

  /** The number to interpolate at; NaN when it is invalid (text, or negative). */
  // LSEG: InterpProcessor (37064).getXValue
  private getXValue(value: unknown): number {
    const cell = firstCell(value);
    this.validateNotEmpty(cell, "X value");
    return nonNegative(this.toNumber(cell));
  }

  /**
   * The cells of a list: a text split at , ; | (blank parts empty), a number, or a single
   * row or column of cells.
   */
  // LSEG: InterpProcessor (37064).getValues
  private getList(value: unknown, fieldName: string): unknown[] {
    this.validateNotEmpty(value, fieldName);
    if (Array.isArray(value)) {
      const rows = value as unknown[][];
      this.adFinUtils.validate(rows.length === 1 || rows[0].length === 1, fieldName, "select a single column or a single row");
      return rows.length === 1 ? rows[0] : rows.map((row) => row[0]);
    }
    return typeof value === "string" ? value.split(LIST_SEPARATORS) : [value];
  }

  /** The method, normalised; it must be a text of the pattern. */
  // LSEG: InterpProcessor (37064).getInterpMethod
  private getInterpMethod(value: unknown): string {
    const fieldName = "interpolation method";
    const cell = firstCell(value);
    this.validateNotEmpty(cell, fieldName);
    const method = typeof cell === "string" ? normaliseMethod(cell) : "";
    this.adFinUtils.validate(METHOD_PATTERN.test(method), fieldName, `name of interpolation method is expected, but found '${cell}' instead`);
    return method;
  }

  /**
   * A cell as a number: a number, a numeric text, or a date text as its Excel
   * serial (through getFormattedDate, as the date functions read dates). NaN for
   * anything else.
   */
  // LSEG: InterpProcessor (37064), its reading of a value or a date (dates become Excel serials)
  private toNumber(cell: unknown): number {
    if (typeof cell === "number") return cell;
    if (typeof cell !== "string") return NaN;
    const text = cell.trim();
    if (NUMERIC_TEXT.test(text)) return Number(text);
    try {
      const iso = this.adFinUtils.getFormattedDate({ date: text });
      return iso ? isoToSerial(iso) : NaN;
    } catch {
      return NaN;
    }
  }
}

/** [A] A range given for a single value is read at its first cell (LSEG's reading of one is not captured). */
function firstCell(value: unknown): unknown {
  return Array.isArray(value) ? (value as unknown[][])[0]?.[0] : value;
}

/** A negative X is invalid input for LSEG (golden x_negative, negative_xs): NaN, and no call. */
function nonNegative(n: number): number {
  return n < 0 ? NaN : n;
}

/** The raw answer `executorResult` reads for a Y value. */
const answer = (value: number): unknown => ({ data: [{ headers: ["Y value"], value }] });

/**
 * The answer to an Interp request, as the interface would have given it
 * (see DateFunctionSpec.local): the Y value, NaN for invalid input (shown as
 * Excel's #N/A, with no call), Adfin's error, or `{ eavText }` for a method
 * LSEG accepts and EAV has not ported yet, or not for these points (MATCHED_ON
 * in interpMethods.ts: CUBS/CUBX on a curve with a Y of 0 or a repeated X,
 * ...), after the same X-list checks.
 */
// LSEG: InterpExecutor (54902).execute (invalid input: the value stays NaN; Adfin's errors: "AdInterp evaluation error: ...")
export function interpLocal(request: Record<string, unknown>): unknown {
  const { xValue, xValues, yValues, interpMethod } = request as unknown as InterpRequest;
  if ([xValue, ...xValues, ...yValues].some(Number.isNaN)) return answer(NaN);
  const error = (message: string) => ({ error: { message: `AdInterp evaluation error: ${message}` } });
  if (LSEG_METHODS.has(interpMethod) && !isPorted(interpMethod, xValues, yValues)) {
    const refused = inputError(xValues);
    return refused ? error(refused) : { eavText: `EAV: interpolation method ${interpMethod} is not available yet` };
  }
  const y = adInterp(xValue, xValues, yValues, interpMethod);
  return "error" in y ? error(y.error) : answer(y.value);
}
